import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { LoadingScreen, NoddiBasicCard } from 'noddi-ui-web';
import { formatPhoneNumber } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { type PropsWithChildren, useEffect } from 'react';
import type { ProposalRecipient } from '../../stores/bookingProposalStore';
import { isUserGroupMember } from '../../utils/userGroupMembership';
import { NewBookingButton } from '../Common/NewBookingButton';
import { RecallContentWrapper } from '../Layouts/RecallContentWrapper';
import { CustomerSMSLogin } from '../Login/CustomerSMSLogin';

interface Props {
  userGroupId: number | undefined;
  recipient?: ProposalRecipient;
  onAccessDenied?: () => void;
}

/** Keeps the phone number on one line. */
const NO_BREAK_SPACE = String.fromCharCode(0xa0);

const getDeniedSubtitle = (recipient: ProposalRecipient | undefined) => {
  if (!recipient) {
    return t`This suggestion belongs to a group you are not a member of. Sign in with another account, or ask the group administrator to add you.`;
  }
  const contact = recipient.contactPhoneNumber
    ? `${recipient.contactName} (${formatPhoneNumber(recipient.contactPhoneNumber).replaceAll(' ', NO_BREAK_SPACE)})`
    : recipient.contactName;
  if (recipient.organizationName) {
    const organizationName = recipient.organizationName;
    return t`This suggestion is for ${organizationName}. Sign in as ${contact} or another member of ${organizationName}, or ask the administrator to add you.`;
  }
  return t`This suggestion is for ${contact}. Sign in with that account to book it.`;
};

/** Account selection does not grant access: any member of the exact group may book. */
export function ProposalAccessGate({ userGroupId, recipient, onAccessDenied, children }: PropsWithChildren<Props>) {
  const { isLoggedIn, userData } = useAuthContext();
  const user = userData?.user;
  const denied =
    userGroupId != null &&
    isLoggedIn &&
    user != null &&
    !user.isStaff &&
    !user.isSuperuser &&
    !isUserGroupMember(userGroupId, user.userGroups);

  useEffect(() => {
    if (denied) {
      onAccessDenied?.();
    }
  }, [denied, onAccessDenied]);

  if (userGroupId != null && isLoggedIn && !user) {
    return <LoadingScreen />;
  }
  if (!denied) {
    return children;
  }

  return (
    <RecallContentWrapper
      title={t`Sign in with an account that has access`}
      subtitle={getDeniedSubtitle(recipient)}
      hideBackButton
    >
      <NoddiBasicCard>
        <CustomerSMSLogin userGroupId={userGroupId} />
      </NoddiBasicCard>
      {/* Way out for someone who only wants their own booking; NewBookingButton clears the drafts,
          so a mobile draft still carrying this proposal's customer cannot gate that booking too. */}
      <div className='mt-4 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground'>
        <Trans>Would you rather book a time for yourself?</Trans>
        <NewBookingButton variant='link' className='p-0' />
      </div>
    </RecallContentWrapper>
  );
}
